import React, { FC } from 'react';
import { defineLoader, httpTaskToResponseTask, useLoader } from '@core/router/loader';
import { SensorsService } from '@modules/sensors/service';
import { z } from 'zod';
import { HubApi } from '@modules/iot/model';
import { defineRoute, preventActionLeave } from '@core/router';
import { Stepper } from '@mantine/core';
import { Sensor } from '@modules/sensors/model';
import { sequenceS } from 'fp-ts/Apply';
import { SensoterraService } from '@modules/sensors/sensoterra/service';
import SensoterraFirstStepForm from '@modules/sensors/sensoterra/components/steps/SensoterraFirstStepForm';
import SensoterraSecondStepForm from '@modules/sensors/sensoterra/components/steps/SensoterraSecondStepForm';
import * as TE from 'fp-ts/TaskEither';
import { ZonesService } from '@modules/iot/zones/service';
import DetailPage from '@modules/sensors/pending/DetailPage';
import { SensoterraUtils } from '@modules/sensors/sensoterra/utils';
import { FieldPath } from 'react-hook-form';
import { Sensoterra } from '@modules/sensors/sensoterra/model';
import RegisterParams = Sensoterra.RegisterParams;
import { defineAction, useAction } from '@core/router/action';
import { PendingSensor } from '@modules/sensors/pending/model';
import typeRegisterSchema = PendingSensor.typeRegisterSchema;
import ThirdStepForm from '@modules/sensors/pending/components/steps/ThirdStepForm';

const params = z.object({ id: HubApi.SensorId });
const type = Sensor.Type.Sensoterra;

const actions = {
  register: defineAction({
    type: 'register',
    payload: typeRegisterSchema[type],
    handler: ({ payload }) => SensorsService.registerPending(type, payload),
    flashOptions: {
      success: () => 'Configuration réalisée',
    },
  }),
};

const loader = defineLoader({
  params,
  handler: ({ params }) =>
    httpTaskToResponseTask(
      sequenceS(TE.ApplyPar)({
        sensor: SensorsService.getPendingSensor<typeof type>(params.id),
        parameters: SensoterraService.getParameters(),
        zones: ZonesService.getZones(),
      }),
    ),
});

const PendingDetailPage: FC = () => {
  const [registerLoading, register] = useAction(actions.register);
  const { sensor, parameters, zones } = useLoader<typeof loader>();

  const checks: Array<Array<FieldPath<RegisterParams>>> = [
    ['name', 'zoneId', 'comment', 'config.soilType'],
    ['config.humidityThreshold'],
    ['config.coordinates'],
  ];

  return (
    <DetailPage
      sensor={sensor}
      loading={registerLoading}
      checks={checks}
      defaultValues={SensoterraUtils.getPendingDefaultValues(sensor)}
      onRegister={register}
    >
      <Stepper.Step>
        <SensoterraFirstStepForm parameters={parameters} depth={sensor.config.depth} zones={zones} />
      </Stepper.Step>
      <Stepper.Step>
        <SensoterraSecondStepForm />
      </Stepper.Step>
      <Stepper.Step>
        <ThirdStepForm type={sensor.type} />
      </Stepper.Step>
    </DetailPage>
  );
};

const pendingSensoterraDetail = defineRoute({
  element: <PendingDetailPage />,
  loader,
  actions,
  shouldRevalidate: preventActionLeave<typeof actions>('register'),
});

export default pendingSensoterraDetail;
